import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNotificationHubContext } from '@/features/notifications/public';
import { EXTERNAL_CALENDAR_EVENTS_KEYS } from './queryKeys';
import type { CalendarEventsSyncedPayload } from '../types';

/**
 * Subscribes to `ReceiveCalendarEventsSynced` on the shared notifications hub.
 *
 * No invoke() subscription needed — the user joins the `user:{userId}` group
 * automatically on connect (unlike board events). The payload is thin (counts
 * only), so the correct reaction is invalidating the external events queries
 * and letting TanStack Query re-fetch — same pattern as useBoardRealtimeEvents.
 */
export function useCalendarSyncRealtimeEvents(orgId: string) {
  const queryClient = useQueryClient();
  const { connectionRef, reconnectCount } = useNotificationHubContext();

  useEffect(() => {
    const connection = connectionRef.current;
    if (!connection || !orgId) return;

    const onSynced = (_payload: CalendarEventsSyncedPayload) => {
      queryClient.invalidateQueries({ queryKey: EXTERNAL_CALENDAR_EVENTS_KEYS.root(orgId) });
    };

    connection.on('ReceiveCalendarEventsSynced', onSynced);

    return () => {
      connection.off('ReceiveCalendarEventsSynced', onSynced);
    };
    // reconnectCount drives re-subscription after reconnect — SignalR lifecycle
    // callbacks cannot be unregistered, so this counter in deps replaces a
    // per-effect callback (same rationale as useBoardRealtimeEvents).
  }, [connectionRef, orgId, queryClient, reconnectCount]);
}
